/**
 * Bank —— 銀行對帳（P2-5；`decisions/008`）
 * ① 匯入月結單（CSV／TSV 貼上，唔准靜默掉行）② 配對建議 → 一撳配對／忽略 ③ 對帳報告（差額一眼睇到）
 * ⚠️ 對帳**唔會改帳**：statement 行本身唔會產生分錄，只係標記。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import { api, fmtMoney, type BankLinesResponse, type BankReport, type Meta } from '../lib/api';
import BankReportView from '../components/BankReport';

export default function Bank({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [accountId, setAccountId] = useState('');
  const [status, setStatus] = useState('unmatched');
  const [data, setData] = useState<BankLinesResponse | null>(null);
  const [report, setReport] = useState<BankReport | null>(null);
  const [csv, setCsv] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<{ row: number; raw: string; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const accounts = meta.accounts.filter((a) => a.entity_id === entityId && (a.kind === 'bank' || a.kind === 'cash'));
  const current = accountId || accounts[0]?.id || '';

  const load = useCallback(() => {
    if (!current) return;
    Promise.all([api.bankLines(current, status), api.bankReport(current)])
      .then(([l, r]) => { setData(l); setReport(r); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [current, status]);
  useEffect(() => { load(); }, [load]);

  const doImport = async () => {
    if (!csv.trim()) { setErr(t('bank.csv_needed')); return; }
    setBusy(true); setErr(null); setOk(null); setImportErrors([]);
    try {
      const r = await api.bankImport(current, csv);
      setOk(t('bank.imported', { imported: r.imported, skipped: r.skipped }));
      setImportErrors(r.errors ?? []);
      setCsv('');
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const act = async (lineId: string, action: 'match' | 'ignore', voucherId?: string) => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await api.bankAct({ lineId, action, voucherId, reason: reasons[lineId] });
      setOk(action === 'match' ? t('bank.matched', { no: r.voucherNo ?? '' }) : t('bank.ignored', { reason: r.reason ?? '' }));
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg.includes('已經處理過') ? t('bank.all_done') : msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <TopBar title={t('bank.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('bank.entity')}</span>
            <select value={entityId} onChange={(e) => { setEntityId(e.target.value); setAccountId(''); }}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('bank.account')}</span>
            <select value={current} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}（{a.gl_code}）</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('bank.status')}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="unmatched">{t('bank.status_unmatched')}</option>
              <option value="matched">{t('bank.status_matched')}</option>
              <option value="ignored">{t('bank.status_ignored')}</option>
              <option value="all">{t('bank.status_all')}</option>
            </select>
          </label>
        </div>
        {data && (
          <p className="totals">
            {t('bank.status_unmatched')}：<b>{(data.summary.unmatched)}</b>　
            {t('bank.status_matched')}：<b>{(data.summary.matched)}</b>　
            {t('bank.status_ignored')}：<b>{(data.summary.ignored)}</b>　
            {t('bank.net')}：<b className={data.summary.unmatchedNet < 0 ? 'neg' : 'pos'}>{fmtMoney(data.summary.unmatchedNet)}</b>
          </p>
        )}
        {err && <p className="err">{err}</p>}
        {ok && <p className="ok">{ok}</p>}
      </section>

      {/* ① 匯入 */}
      <section className="card">
        <h2>{t('bank.import_heading')}</h2>
        <p className="note">{t('bank.import_hint')}</p>
        <textarea
          className="raw" rows={6} value={csv} onChange={(e) => setCsv(e.target.value)}
          placeholder={'日期,摘要,金額,餘額\n01/09/2026,存入 JHC 貨款,"12,000.00","12,000.00"'}
        />
        <button className="cta" onClick={() => void doImport()} disabled={busy || !csv.trim()}>{t('bank.import_cta')}</button>
        {importErrors.length > 0 && (
          <>
            <p className="err">{t('bank.import_errors', { n: importErrors.length })}</p>
            <ul className="warnlist">
              {importErrors.map((e, i) => (
                <li key={i}>第 {e.row} 行：{e.reason}｜<code>{e.raw.slice(0, 80)}</code></li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* ② 月結單行 ＋ 配對建議 */}
      <section className="card">
        <h2>{t('bank.status')}：{status === 'all' ? t('bank.status_all') : t(`bank.status_${status}`)}</h2>
        {!data || data.lines.length === 0 ? <p className="note">{t('bank.empty')}</p> : (
          <ul className="list">
            {data.lines.map((l) => (
              <li key={l.id}>
                <span className="tag">{l.txn_date}</span>
                <span>{l.description ?? '—'}</span>
                <b className={l.amount_cents < 0 ? 'neg' : 'pos'}>
                  {l.amount_cents < 0 ? t('bank.direction_out') : t('bank.direction_in')} {fmtMoney(Math.abs(l.amount_cents))}
                </b>
                {l.status === 'matched' && <span className="ok">✅ {t('bank.status_matched')}</span>}
                {l.status === 'ignored' && <span className="note">🚫 {l.ignored_reason}</span>}
                {l.status === 'unmatched' && (
                  <span className="row-warn">
                    {l.suggestions.length === 0
                      ? <small className="note">{t('bank.no_suggestion')}</small>
                      : l.suggestions.map((s) => (
                        <button key={s.voucher_id} onClick={() => void act(l.id, 'match', s.voucher_id)} disabled={busy}>
                          {t('bank.match')} {s.voucher_no}（{s.txn_date}）
                        </button>
                      ))}
                    <input
                      className="budget-input" placeholder={t('bank.ignore_reason_hint')}
                      value={reasons[l.id] ?? ''} onChange={(e) => setReasons((r) => ({ ...r, [l.id]: e.target.value }))}
                    />
                    <button onClick={() => void act(l.id, 'ignore')} disabled={busy || (reasons[l.id] ?? '').trim().length < 3}>
                      {t('bank.ignore')}
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ③ 對帳報告（內容抽去 `components/BankReport.tsx`，令本頁 ≤200 行）*/}
      <section className="card">
        <h2>{t('bank.report_heading')}</h2>
        <BankReportView report={report} />
      </section>
    </div>
  );
}
